import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { FORMULA_DISPLAY_ALLOWED_INSERT_CHARS } from 'src/utils/columns/customColumnUtils';
import { Feature } from 'src/WebApiController';

import { CustomColumnType } from '../TableCustomColumnModal.types';

export const useFormulaBuilderActions = (
  formulaTokens: string[],
  formulaCaretPosition: number,
  setFormulaTokens: (tokens: string[]) => void,
  setFormulaCaretPosition: (position: number) => void,
  setShowMetricSelector: (show: boolean) => void,
  setShowMathOperatorSelector: (show: boolean) => void
) => {
  const hasMinMaxInCustomColumnFeature = useUserHasFeature(
    Feature.MinMaxInCustomColumn
  );
  const sanitizedAllowedInsertChars = useMemo(() => {
    if (hasMinMaxInCustomColumnFeature) {
      return FORMULA_DISPLAY_ALLOWED_INSERT_CHARS;
    }
    return FORMULA_DISPLAY_ALLOWED_INSERT_CHARS.filter(
      (char) => char !== 'm' && char !== ','
    );
  }, [hasMinMaxInCustomColumnFeature]);

  const formulaTokensWithCaret = useMemo(() => {
    if (formulaCaretPosition === -1) return formulaTokens;
    const result = [...formulaTokens];
    result.splice(formulaCaretPosition, 0, '|');
    return result;
  }, [formulaCaretPosition, formulaTokens]);

  const keyUpHandler = useCallback(
    (e: KeyboardEvent) => {
      // Keys are ignored if the editor is out of focus (no caret)
      if (formulaCaretPosition === -1) return;
      if (sanitizedAllowedInsertChars.includes(e.key)) {
        if (
          // Number pressed and the previous token is number --> append
          /[0-9]/.test(e.key) &&
          formulaCaretPosition > 0 &&
          /[0-9]/.test(formulaTokens[formulaCaretPosition - 1])
        ) {
          const newTokens = [...formulaTokens];
          newTokens[formulaCaretPosition - 1] =
            formulaTokens[formulaCaretPosition - 1] + e.key;
          setFormulaTokens(newTokens);
        } else if (
          // Number pressed and the previous next is number --> prepend
          /[0-9]/.test(e.key) &&
          formulaCaretPosition < formulaTokens.length &&
          /[0-9]/.test(formulaTokens[formulaCaretPosition])
        ) {
          const newTokens = [...formulaTokens];
          newTokens[formulaCaretPosition] =
            e.key + formulaTokens[formulaCaretPosition];
          setFormulaTokens(newTokens);
        } else if (e.key === '@') {
          // @ --> open the metric selector dropdown
          setShowMetricSelector(true);
        } else if (hasMinMaxInCustomColumnFeature && e.key === 'm') {
          setShowMathOperatorSelector(true);
        } else {
          // else: replace the caret with the typed char (number or operator) and set new caret
          const newTokens = [...formulaTokensWithCaret].map((token) =>
            token === '|' ? e.key : token
          );
          setFormulaTokens(newTokens);
          setFormulaCaretPosition(formulaCaretPosition + 1);
        }
      } else if (e.key === 'ArrowLeft') {
        // move caret left
        setFormulaCaretPosition(formulaCaretPosition - 1);
      } else if (e.key === 'ArrowRight') {
        // move caret right
        setFormulaCaretPosition(formulaCaretPosition + 1);
      } else if (e.key === 'Backspace') {
        // delete one token before the caret
        const newTokens = [...formulaTokens];
        newTokens.splice(formulaCaretPosition - 1, 1);
        setFormulaTokens(newTokens);
        setFormulaCaretPosition(formulaCaretPosition - 1);
        setShowMathOperatorSelector(false);
        setShowMetricSelector(false);
      } else if (e.key === 'Delete') {
        // delete one token after the caret
        const newTokens = [...formulaTokens];
        newTokens.splice(formulaCaretPosition, 1);
        setFormulaTokens(newTokens);
        setShowMathOperatorSelector(false);
        setShowMetricSelector(false);
      }
    },
    [
      formulaCaretPosition,
      formulaTokens,
      formulaTokensWithCaret,
      hasMinMaxInCustomColumnFeature,
      setFormulaCaretPosition,
      setFormulaTokens,
      setShowMathOperatorSelector,
      setShowMetricSelector,
    ]
  );
  useEffect(() => {
    window.addEventListener('keyup', keyUpHandler);

    return () => {
      window.removeEventListener('keyup', keyUpHandler);
    };
  }, [keyUpHandler]);

  const tokenRefs = useRef<HTMLSpanElement[]>([]);
  const {
    formState: { errors },
    clearErrors,
  } = useFormContext<CustomColumnType>();
  useEffect(() => {
    // this ensures keeping refs up-to-date for click tracking & caret positioning
    tokenRefs.current = Array(formulaTokens.length).fill(null);
    clearErrors('formula');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formulaTokens]);

  const onBuilderInputClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      e.stopPropagation();
      const tokenRowRange = formulaTokens
        .map((token, i) => ({
          i,
          token,
        }))
        .filter((tokenItem) => {
          const tokenElement = tokenRefs.current[tokenItem.i];
          if (!tokenElement) return false;
          const rect = tokenElement.getBoundingClientRect();
          return e.clientY >= rect.y && e.clientY <= rect.y + rect.height;
        });
      if (tokenRowRange.length === 0) {
        setFormulaCaretPosition(formulaTokens.length);
        return;
      }
      const position = tokenRowRange.find((tokenItem) => {
        const tokenElement = tokenRefs.current[tokenItem.i];
        if (!tokenElement) return false;
        const rect = tokenElement.getBoundingClientRect();
        return rect.x > e.clientX;
      });
      if (position) {
        setFormulaCaretPosition(position.i);
      } else {
        // none of them were left of the click
        const lastInRow = tokenRowRange.at(-1) as {
          i: number;
          token: string;
        };
        setFormulaCaretPosition(lastInRow.i + 1);
      }
    },
    [formulaTokens, setFormulaCaretPosition]
  );

  const onRegisterTokenRef = useCallback(
    (index: number) => (element: HTMLSpanElement | null) => {
      if (element) {
        tokenRefs.current[index] = element;
      }
    },
    []
  );

  return {
    formulaTokensWithCaret,
    formulaError: errors.formula,
    onBuilderInputClick,
    onRegisterTokenRef,
  };
};
